/** Browser Gemini Live session for Kai speech mode. */

export type LiveStatus = "connecting" | "listening" | "speaking" | "error";

export type LiveTurn = {
  userText: string;
  kaiText: string;
};

export type KaiLiveCallbacks = {
  onStatus?: (status: LiveStatus) => void;
  onOutputTranscript?: (text: string) => void;
  onInputTranscript?: (text: string) => void;
  onTurnComplete?: (turn: LiveTurn) => void;
  onError?: (message: string) => void;
};

type LiveTokenResponse = {
  token?: string;
  model?: string;
  error?: string;
};

const INPUT_RATE = 16_000;
const OUTPUT_RATE = 24_000;
const CHUNK_SAMPLES = 2048;

function floatTo16BitPcm(float32: Float32Array): ArrayBuffer {
  const buf = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToInt16(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}

function downsample(
  input: Float32Array,
  inputRate: number,
  outputRate: number,
): Float32Array {
  if (inputRate === outputRate) return input;
  const ratio = inputRate / outputRate;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const result = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    result[i] = sum / Math.max(1, end - start);
  }
  return result;
}

export class KaiLiveSession {
  private ws: WebSocket | null = null;
  private micStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private playCtx: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private nextPlayTime = 0;
  private playSources = new Set<AudioBufferSourceNode>();
  private closed = false;
  private model = "gemini-3.8-live";
  private inputParts: string[] = [];
  private outputParts: string[] = [];
  private speaking = false;
  private readonly callbacks: KaiLiveCallbacks;

  constructor(callbacks: KaiLiveCallbacks = {}) {
    this.callbacks = callbacks;
  }

  async start(): Promise<void> {
    this.closed = false;
    this.callbacks.onStatus?.("connecting");

    const tokenRes = await fetch("/api/live-token", { method: "POST" });
    const tokenData = (await tokenRes.json().catch(() => ({}))) as LiveTokenResponse;
    if (!tokenRes.ok || !tokenData.token) {
      throw new Error(tokenData.error || "Could not start speech mode.");
    }
    this.model = tokenData.model || this.model;

    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
      },
    });

    await this.openSocket(tokenData.token);
    await this.startCapture();
    this.callbacks.onStatus?.("listening");
  }

  /** End the session. Pass `{ commit: false }` on unmount to skip chat log updates. */
  stop(opts?: { commit?: boolean }): LiveTurn | null {
    const pending = this.collectTurn();
    if (opts?.commit !== false && pending) {
      this.callbacks.onTurnComplete?.(pending);
    }
    this.teardown();
    return pending;
  }

  private collectTurn(): LiveTurn | null {
    const userText = this.inputParts.join("").trim();
    const kaiText = this.outputParts.join("").trim();
    this.inputParts = [];
    this.outputParts = [];
    if (!userText && !kaiText) return null;
    return { userText, kaiText };
  }

  private flushTurn() {
    const turn = this.collectTurn();
    if (turn) this.callbacks.onTurnComplete?.(turn);
  }

  private async openSocket(token: string): Promise<void> {
    const url =
      "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained" +
      `?access_token=${encodeURIComponent(token)}`;

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url);
      this.ws = ws;
      let settled = false;

      const fail = (message: string) => {
        if (settled) return;
        settled = true;
        reject(new Error(message));
      };

      ws.onopen = () => {
        // Token already locks full BidiGenerateContentSetup; send model to start.
        ws.send(
          JSON.stringify({
            setup: { model: `models/${this.model}` },
          }),
        );
      };

      ws.onmessage = async (event) => {
        try {
          const raw =
            typeof event.data === "string"
              ? event.data
              : await (event.data as Blob).text();
          const msg = JSON.parse(raw) as Record<string, unknown>;

          if (msg.setupComplete !== undefined) {
            if (!settled) {
              settled = true;
              resolve();
            }
            return;
          }

          if (msg.error) {
            const err = msg.error as { message?: string };
            const message = err.message || "Live session error";
            this.callbacks.onError?.(message);
            this.callbacks.onStatus?.("error");
            fail(message);
            return;
          }

          this.handleServerMessage(msg);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Bad live message";
          this.callbacks.onError?.(message);
        }
      };

      ws.onerror = () => {
        this.callbacks.onStatus?.("error");
        fail("Speech connection failed.");
      };

      ws.onclose = () => {
        if (!settled) fail("Speech connection closed.");
        if (!this.closed) {
          this.callbacks.onStatus?.("error");
          this.callbacks.onError?.("Speech connection closed.");
        }
      };
    });
  }

  private handleServerMessage(msg: Record<string, unknown>) {
    const sc = msg.serverContent as
      | {
          interrupted?: boolean;
          turnComplete?: boolean;
          inputTranscription?: { text?: string };
          outputTranscription?: { text?: string };
          modelTurn?: { parts?: Array<{ inlineData?: { data?: string; mimeType?: string } }> };
        }
      | undefined;

    if (!sc) return;

    if (sc.interrupted) {
      this.stopPlayback();
      this.speaking = false;
      this.callbacks.onStatus?.("listening");
    }

    if (sc.inputTranscription?.text) {
      this.inputParts.push(sc.inputTranscription.text);
      this.callbacks.onInputTranscript?.(this.inputParts.join(""));
    }

    if (sc.outputTranscription?.text) {
      this.outputParts.push(sc.outputTranscription.text);
      this.callbacks.onOutputTranscript?.(this.outputParts.join(""));
      if (!this.speaking) {
        this.speaking = true;
        this.callbacks.onStatus?.("speaking");
      }
    }

    const parts = sc.modelTurn?.parts || [];
    for (const part of parts) {
      const data = part.inlineData?.data;
      if (data) void this.enqueuePlayback(data);
    }

    if (sc.turnComplete) {
      this.speaking = false;
      this.flushTurn();
      this.callbacks.onStatus?.("listening");
      this.callbacks.onOutputTranscript?.("");
      this.callbacks.onInputTranscript?.("");
    }
  }

  private async startCapture(): Promise<void> {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.captureCtx = new AudioCtx();
    if (this.captureCtx.state === "suspended") await this.captureCtx.resume();

    this.source = this.captureCtx.createMediaStreamSource(this.micStream!);
    this.processor = this.captureCtx.createScriptProcessor(CHUNK_SAMPLES, 1, 1);
    const silent = this.captureCtx.createGain();
    silent.gain.value = 0;
    this.processor.onaudioprocess = (event) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.closed) return;
      const input = event.inputBuffer.getChannelData(0);
      const pcm = downsample(input, this.captureCtx!.sampleRate, INPUT_RATE);
      const b64 = arrayBufferToBase64(floatTo16BitPcm(pcm));
      this.ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: {
              mimeType: `audio/pcm;rate=${INPUT_RATE}`,
              data: b64,
            },
          },
        }),
      );
    };

    // ScriptProcessor only runs while connected; mute so mic isn't played locally.
    this.source.connect(this.processor);
    this.processor.connect(silent);
    silent.connect(this.captureCtx.destination);
  }

  private async ensurePlayCtx(): Promise<AudioContext> {
    if (!this.playCtx) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.playCtx = new AudioCtx({ sampleRate: OUTPUT_RATE });
    }
    if (this.playCtx.state === "suspended") await this.playCtx.resume();
    return this.playCtx;
  }

  private async enqueuePlayback(base64: string): Promise<void> {
    try {
      const ctx = await this.ensurePlayCtx();
      const int16 = base64ToInt16(base64);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;

      const buffer = ctx.createBuffer(1, float32.length, OUTPUT_RATE);
      buffer.copyToChannel(float32, 0);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);

      const startAt = Math.max(ctx.currentTime, this.nextPlayTime);
      source.start(startAt);
      this.nextPlayTime = startAt + buffer.duration;
      this.playSources.add(source);
      source.onended = () => this.playSources.delete(source);

      if (!this.speaking) {
        this.speaking = true;
        this.callbacks.onStatus?.("speaking");
      }
    } catch {
      // Ignore decode/play glitches; session can continue.
    }
  }

  private stopPlayback() {
    for (const source of this.playSources) {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
    }
    this.playSources.clear();
    this.nextPlayTime = 0;
  }

  private teardown() {
    this.closed = true;
    this.stopPlayback();

    try {
      this.processor?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      this.source?.disconnect();
    } catch {
      /* ignore */
    }
    this.processor = null;
    this.source = null;

    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;

    void this.captureCtx?.close();
    void this.playCtx?.close();
    this.captureCtx = null;
    this.playCtx = null;

    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }
}
